import { Box, withStyles, Icon, Table, formattedCurrency, removeWhiteSpaceURI } from 'vopay';
import type { SupportedCurrencies, InterestAccountTransactionsResponse } from 'vopay';
import { FunctionComponent } from 'react';
import { Link } from 'react-router-dom';
import styles from './InterestAccountTransactionTable.module.scss';
import { formatDate } from './utils';
import { useTranslation } from 'react-i18next';

const cn = withStyles(styles);

type FC = FunctionComponent<InterestAccountTransactionTableProps>;

type InterestAccountTransactionTableProps = {
  id: string;
  fetching: boolean;
  transactions: InterestAccountTransactionsResponse['InterestAccountTransactions'] | null; // null means we haven't initialized yet
  currency: SupportedCurrencies;
};

const InterestAccountTransactionTable: FC = ({
  id,
  fetching = false,
  transactions,
  currency,
}: InterestAccountTransactionTableProps) => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'Accounts.InterestAccountsDrawer.TransactionsTab',
  });

  return (
    <div className={cn('interestAccountTransactionTableContainer', 'scrollbar')}>
      {transactions !== null && (
        <Table
          zebra
          id={id}
          style={{
            '--headerBackground': '#2d2d2d',
            '--headerColor': '#fbfbfb',
            '--headerBorderRadius': '14px',
            '--thBorderBottom': 'none',
          }}
          version="2"
          rows={
            fetching
              ? []
              : transactions.map((transaction) => ({
                  id: transaction.TransactionID,
                  data: transaction,
                }))
          }
          columns={[
            {
              id: 'TransactionDate',
              header: t('TransactionDate.column', { defaultValue: 'Date' }),
              align: 'left',
              template: ({ data }) => formatDate(data.TransactionDate),
              style: {
                whiteSpace: 'nowrap',
              },
            },
            {
              id: 'TransactionID',
              header: t('TransactionID.column', { defaultValue: 'Transaction ID' }),
              align: 'left',
              template: ({ data }) => (
                <Box className={cn('interestAccountTransactionLink')}>
                  <Link
                    to={{
                      pathname: `/transactions-history/details/${removeWhiteSpaceURI(data.AccountType)}/${
                        data.TransactionID
                      }/${currency}`,
                      state: {
                        transactionId: data.TransactionID,
                        hasErrorInfo: data.Status === 'failed',
                      },
                    }}
                  >
                    {data.TransactionID}
                  </Link>
                </Box>
              ),
            },
            {
              id: 'TransactionType',
              header: t('TransactionType.column', { defaultValue: 'Trans. Type' }),
              align: 'left',
              field: 'TransactionType',
            },
            {
              id: 'CreditAmount',
              header: t('CreditAmount.column', { defaultValue: 'Credit' }),
              align: 'left',
              template: ({ data }) =>
                data.CreditAmount === '0.00' ? '-' : `${currency} ${formattedCurrency(data.CreditAmount)}`,
              style: {
                whiteSpace: 'nowrap',
              },
            },
            {
              id: 'DebitAmount',
              header: t('DebitAmount.column', { defaultValue: 'Debit' }),
              align: 'left',
              template: ({ data }) =>
                data.DebitAmount === '0.00' ? '-' : `${currency} ${formattedCurrency(data.DebitAmount)}`,
              style: {
                whiteSpace: 'nowrap',
              },
            },
            {
              id: 'AccountType',
              header: t('AccountType.column', { defaultValue: 'Account Type' }),
              align: 'left',
              field: 'AccountType',
            },
            {
              id: 'EligibilityDate',
              header: t('EligibilityDate.column', { defaultValue: 'Eligibility Date' }),
              align: 'left',
              template: ({ data }) => formatDate(data.EligibilityDate),
              style: {
                whiteSpace: 'nowrap',
              },
            },
          ]}
        />
      )}

      {fetching ? (
        <div className={cn('spinner')}>
          <Icon icon="loader" size="xl-jumbo" />
        </div>
      ) : (
        transactions?.length === 0 && (
          <div className={cn('transactionTableEmptyContainer')}>
            <div>
              {t('EmptyContainer.noResults', {
                defaultValue: 'There are no records to display within this date range',
              })}
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default InterestAccountTransactionTable;
